// @ts-check
import { z } from 'zod';

import {
  documentsSchema,
  updateDocumentsSchema,
} from '@/data/schema/documents';
import { partiesSchema } from '@/data/schema/parties';
import Permission from '@/store/modules/models/global/Permission';
import Timestamp from '@/store/modules/models/global/Timestamp';
import { SharingSummary } from '@/store/modules/users/models';

export const Envelope = z.object({
  auto_remind_hours: z.number().nullable(),
  company_id: z.number(),
  created_at: Timestamp,
  deleted_at: Timestamp,
  everyone_permission: Permission.nullable(),
  fingerprint: z.string(),
  id: z.number(),
  is_parallel_signing_enabled: z.boolean().nullable(),
  meta_data: z.string(),
  next_expire: Timestamp,
  next_remind: Timestamp,
  parties: partiesSchema,
  processed_at: Timestamp,
  redirect_url: z.string().nullable(),
  rejection_party_id: z.number().nullable(),
  rejection_reason: z.string().nullable(),
  reminded: Timestamp,
  sent_at: Timestamp,
  sharing: SharingSummary,
  status: z.enum([
    'processing',
    'failed',
    'initial',
    'draft',
    'sent',
    'signed',
    'rejected',
    'verify',
    'cancelled',
    'expired',
  ]),
  thumbnail: z.string().nullable(),
  title: z.string(),
  user_id: z.number().nullable(),
  documents: documentsSchema,
  steps_progress: z
    .object({
      hash: z.coerce.number(),
      index: z.number(),
      updated_at: Timestamp,
    })
    .nullable(),
});

export const ListEnvelope = Envelope.pick({
  fingerprint: true,
  title: true,
  created_at: true,
  status: true,
  thumbnail: true,
  is_parallel_signing_enabled: true,
}).extend({
  parties: Envelope.shape.parties.element
    .pick({
      title: true,
      selected: true,
      email: true,
      id: true,
    })
    .extend({
      status: Envelope.shape.parties.element.shape.status.pick({
        action_performed_date: true,
        sent: true,
        computed_status: true,
        computed_status_date: true,
      }),
    })
    .array(),
});

export const CreatingEnvelope = Envelope.pick({
  fingerprint: true,
  title: true,
  status: true,
  created_at: true,
  sent_at: true,
  reminded: true,
  auto_remind_hours: true,
  next_expire: true,
  processed_at: true,
  user_id: true,
  steps_progress: true,
}).extend({
  parallel_signing: Envelope.shape.is_parallel_signing_enabled,
});

export const NameUpdate = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'ui.envelopes.create.validation.titleTooShort')
    .max(200, 'ui.envelopes.create.validation.titleTooLong'),
});

export const EnvelopeUpdate = z.object({
  title: z
    .string()
    .trim()
    .max(200, 'ui.envelopes.create.validation.titleTooLong')
    .optional(),
  documents: updateDocumentsSchema
    .min(1, 'ui.envelopes.create.validation.noDocuments')
    .optional(),
  parallel_signing: z.boolean().optional(),
  next_expire: Timestamp.optional(),
  auto_remind_hours: z
    .number({
      required_error: 'ui.envelopes.create.validation.needsCustomReminder',
      invalid_type_error: 'ui.envelopes.create.validation.needsCustomReminder',
    })
    .optional(),
});

export const EnvelopeProgressUpdate = z.object({
  steps_progress: z
    .object({
      hash: z.number(),
      index: z.number(),
    }).optional(),
});

export const EnvelopeDocumentsUpdate = EnvelopeProgressUpdate.merge(EnvelopeUpdate.pick({
  title: true,
  documents: true,
}).required());

export const EnvelopeSend = z.object({
  envelope_progressed_at: Timestamp.optional(),
});

/** @typedef {z.infer<typeof Envelope>} TEnvelope */
/** @typedef {z.infer<typeof ListEnvelope>} TListEnvelope */
/** @typedef {z.infer<typeof CreatingEnvelope>} TCreatingEnvelope */
/** @typedef {z.infer<typeof EnvelopeUpdate>} TEnvelopeUpdate */
/** @typedef {z.infer<typeof EnvelopeSend>} TEnvelopeSend */
/** @typedef {z.infer<typeof EnvelopeProgressUpdate>} TEnvelopeProgressUpdate */
/** @typedef {z.infer<typeof EnvelopeDocumentsUpdate>} TEnvelopeDocumentsUpdate */
